import React from "react";
import Slider, { SliderThumb } from "@mui/material/Slider";
import { styled } from "@mui/material/styles";

const AirbnbSlider = styled(Slider)(({ theme }) => ({
  color: "#A3A3A3",
  height: 3,
  padding: "13px 0",
  "& .MuiSlider-thumb": {
    height: 27,
    width: 27,
    backgroundColor: "#fff",
    border: "1px solid #A3A3A3",
    "&:hover": {
      boxShadow: "0 0 0 8px #A3A3A3",
    },
    "& .airbnb-bar": {
      height: 9,
      width: 1,
      backgroundColor: "#A3A3A3",
      marginLeft: 1,
      marginRight: 1,
    },
  },
  "& .MuiSlider-track": {
    height: 3,
  },
  "& .MuiSlider-rail": {
    color: theme.palette.mode === "dark" ? "#bfbfbf" : "#d8d8d8",
    opacity: theme.palette.mode === "dark" ? undefined : 1,
    height: 3,
  },
}));

const AirbnbSliderGraph = styled(Slider)(({ theme }) => ({
  color: "#A3A3A3",
  height: 3,
  padding: "13px 0",
  "& .MuiSlider-thumb": {
    height: 27,
    width: 27,
    backgroundColor: "#fff",
    border: "1px solid #A3A3A3",
    "&:hover": {
      boxShadow: "0 0 0 8px #A3A3A3",
    },
    "& .airbnb-bar": {
      height: 9,
      width: 1,
      backgroundColor: "#A3A3A3",
      marginLeft: 1,
      marginRight: 1,
    },
  },
  "& .MuiSlider-track": {
    height: 3,
    backgroundColor: "transparent",
  },
  "& .MuiSlider-rail": {
    opacity: 0,
    height: 3,
  },
}));

const AirbnbThumbComponent = (props) => {
  const { children, ...other } = props;
  return (
    <SliderThumb {...other}>
      {children}
      <span className="airbnb-bar" />
      <span className="airbnb-bar" />
      <span className="airbnb-bar" />
    </SliderThumb>
  );
};

const CustomSlider = ({
  SelectedRange,
  setSelectedRange,
  min = 0,
  max = 100,
  step = 1,
  onRangeChange,
  showGraph = false,
  graphBars = 24,
}) => {
  const isRange = Array.isArray(SelectedRange) && SelectedRange.length === 2;
  const safeMin = Number.isFinite(Number(min)) ? Number(min) : 0;
  const safeMax = Number.isFinite(Number(max)) ? Number(max) : 100;
  const valueMin = isRange ? Number(SelectedRange[0]) : safeMin;
  const valueMax = isRange ? Number(SelectedRange[1]) : safeMax;
  const leftPct =
    safeMax > safeMin ? ((valueMin - safeMin) / (safeMax - safeMin)) * 100 : 0;
  const rightPct =
    safeMax > safeMin ? ((valueMax - safeMin) / (safeMax - safeMin)) * 100 : 100;
  const clampedLeftPct = Math.max(0, Math.min(100, leftPct));
  const clampedRightPct = Math.max(0, Math.min(100, rightPct));

  const heights = Array.from({ length: graphBars }, (_, i) => {
    const t = graphBars <= 1 ? 0 : i / (graphBars - 1);
    const wave = Math.sin(Math.PI * t);
    const fade = 1 - 0.6 * t;
    const normalized = Math.max(0, wave * fade);
    return 8 + Math.round(normalized * 80);
  });

  const handleChange = (event, value) => {
    if (onRangeChange) {
      onRangeChange(value);
      return;
    }
    if (setSelectedRange) {
      setSelectedRange(value);
    }
  };

  const SliderComponent = showGraph ? AirbnbSliderGraph : AirbnbSlider;

  return (
    <div className="relative w-full mt-10">
      {showGraph && (
        <div className="pointer-events-none absolute left-0 right-0 -top-2 -translate-y-1/2 h-10">
          <div className="flex h-full items-end gap-[2px] px-1">
            {heights.map((h, idx) => {
              const barPct =
                graphBars <= 1 ? 0 : (idx / (graphBars - 1)) * 100;
              const isSelected =
                barPct >= clampedLeftPct && barPct <= clampedRightPct;
              return (
                <div
                  key={idx}
                  className="flex-1 rounded-sm"
                  style={{
                    height: `${h}px`,
                    backgroundColor: isSelected ? "#127DDF" : "#CBD5E1",
                    opacity: isSelected ? 0.95 : 0.7,
                  }}
                />
              );
            })}
          </div>
        </div>
      )}
      <SliderComponent
        slots={{ thumb: AirbnbThumbComponent }}
        value={SelectedRange}
        onChange={handleChange}
        min={safeMin}
        max={safeMax}
        step={step}
        disableSwap={isRange}
        getAriaLabel={(index) =>
          index === 0 ? "Minimum price" : "Maximum price"
        }
      />
    </div>
  );
};

export default CustomSlider;
